import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Card, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, IconButton, Button, TextField, Tooltip,
  Alert, Skeleton, InputAdornment, Select, MenuItem, FormControl,
  InputLabel, Divider, Checkbox, FormControlLabel, Avatar,
} from '@mui/material';
import {
  AddOutlined, EditOutlined, DeleteOutlined, SearchOutlined,
  ShieldOutlined, BusinessOutlined, ExpandMoreOutlined, ExpandLessOutlined,
  ChevronLeftOutlined, ChevronRightOutlined,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { api } from '../../hooks/useApi';
import SlidePanel from '../../components/SlidePanel';

const PRIMARY   = '#1075BA';
const PAGE_SIZE = 8;

// Fixed column widths — applied identically in every group table so all columns align visually
const COL = {
  role:    { width: '35%' },
  type:    { width: 110 },
  perms:   { width: 160 },
  actions: { width: 90, align: 'right' },
};

// ── Permission config ─────────────────────────────────────────────────────────
const PERMISSION_GROUPS = [
  { label: 'Events',       permissions: [{ key: 'events:view', label: 'View events' }, { key: 'events:create', label: 'Create new events' }, { key: 'events:edit', label: 'Edit events' }, { key: 'events:delete', label: 'Delete events' }, { key: 'events:publish', label: 'Publish events' }] },
  { label: 'Forms',        permissions: [{ key: 'forms:view', label: 'View forms' }, { key: 'forms:create', label: 'Create new forms' }, { key: 'forms:edit', label: 'Edit forms' }, { key: 'forms:delete', label: 'Delete forms' }, { key: 'forms:publish', label: 'Publish forms' }] },
  { label: 'Submissions',  permissions: [{ key: 'submissions:view', label: 'View submissions' }, { key: 'submissions:export', label: 'Export submissions' }, { key: 'submissions:delete', label: 'Delete submissions' }] },
  { label: 'Participants', permissions: [{ key: 'participants:view', label: 'View participants' }, { key: 'participants:edit', label: 'Edit participants' }, { key: 'participants:delete', label: 'Delete participants' }, { key: 'participants:export', label: 'Export participants' }] },
  { label: 'Users',        permissions: [{ key: 'users:view', label: 'View admin users' }, { key: 'users:invite', label: 'Invite admin users' }, { key: 'users:edit', label: 'Edit admin users' }, { key: 'users:delete', label: 'Delete admin users' }] },
  { label: 'Roles',        permissions: [{ key: 'roles:view', label: 'View roles' }, { key: 'roles:create', label: 'Create roles' }, { key: 'roles:edit', label: 'Edit roles' }, { key: 'roles:delete', label: 'Delete roles' }] },
  { label: 'Organization', permissions: [{ key: 'organization:view', label: 'View organization settings' }, { key: 'organization:edit', label: 'Edit organization settings' }] },
  { label: 'Analytics',    permissions: [{ key: 'analytics:view', label: 'View analytics & reports' }, { key: 'reports:export', label: 'Export reports' }] },
  { label: 'Emails',       permissions: [{ key: 'emails:view', label: 'View email templates' }, { key: 'emails:send', label: 'Send emails' }, { key: 'emails:edit', label: 'Edit email templates' }] },
];
const ALL_KEYS = PERMISSION_GROUPS.flatMap(g => g.permissions.map(p => p.key));

// ── Shared Pagination ─────────────────────────────────────────────────────────
function Pagination({ page, totalPages, onChange }) {
  if (totalPages <= 1) return null;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75, py: 1.5, borderTop: `1px solid ${alpha(PRIMARY, 0.07)}` }}>
      <IconButton size="small" disabled={page === 1} onClick={() => onChange(page - 1)}
        sx={{ border: `1px solid ${alpha(PRIMARY, 0.2)}`, borderRadius: 1.5, width: 28, height: 28, '&:hover:not(:disabled)': { background: alpha(PRIMARY, 0.06) } }}>
        <ChevronLeftOutlined sx={{ fontSize: 16 }} />
      </IconButton>

      {[...Array(totalPages)].map((_, i) => {
        const p = i + 1;
        const show = p === 1 || p === totalPages || Math.abs(p - page) <= 1;
        const isEllipsis = !show && (p === 2 || p === totalPages - 1);
        if (isEllipsis) return <Typography key={p} sx={{ color: 'text.secondary', fontSize: '0.78rem', px: 0.25 }}>…</Typography>;
        if (!show) return null;
        return (
          <Box key={p} onClick={() => onChange(p)} sx={{
            width: 28, height: 28, borderRadius: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', fontSize: '0.78rem', fontWeight: p === page ? 700 : 500,
            color: p === page ? '#fff' : 'text.secondary',
            background: p === page ? PRIMARY : 'transparent',
            border: `1px solid ${p === page ? PRIMARY : alpha(PRIMARY, 0.2)}`,
            transition: 'all 0.12s',
            '&:hover': { background: p === page ? PRIMARY : alpha(PRIMARY, 0.06) },
          }}>
            {p}
          </Box>
        );
      })}

      <IconButton size="small" disabled={page === totalPages} onClick={() => onChange(page + 1)}
        sx={{ border: `1px solid ${alpha(PRIMARY, 0.2)}`, borderRadius: 1.5, width: 28, height: 28, '&:hover:not(:disabled)': { background: alpha(PRIMARY, 0.06) } }}>
        <ChevronRightOutlined sx={{ fontSize: 16 }} />
      </IconButton>
    </Box>
  );
}

// ── Permissions list (edit panel) ─────────────────────────────────────────────
function PermissionsList({ perms, isSuperAdmin, onToggle, onToggleGroup }) {
  return (
    <Box>
      {PERMISSION_GROUPS.map((group, gi) => {
        const keys       = group.permissions.map(p => p.key);
        const allChecked = isSuperAdmin || keys.every(k => !!perms[k]);
        const anyChecked = keys.some(k => !!perms[k]);
        return (
          <Box key={group.label}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', py: 1.5 }}>
              <Box sx={{ width: 130, flexShrink: 0, pt: 0.25, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Checkbox checked={allChecked} indeterminate={!allChecked && anyChecked} disabled={isSuperAdmin}
                  onChange={() => onToggleGroup(group, !allChecked)} size="small"
                  sx={{ p: 0.25, color: alpha(PRIMARY, 0.3), '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: PRIMARY } }} />
                <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: 'text.primary', lineHeight: 1.3 }}>{group.label}</Typography>
              </Box>
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.1 }}>
                {group.permissions.map(perm => {
                  const checked = isSuperAdmin || !!perms[perm.key];
                  return (
                    <FormControlLabel key={perm.key}
                      control={<Checkbox checked={checked} disabled={isSuperAdmin} onChange={() => onToggle(perm.key, !perms[perm.key])} size="small"
                        sx={{ py: 0.4, px: 0.75, color: alpha(PRIMARY, 0.3), '&.Mui-checked': { color: PRIMARY } }} />}
                      label={<Typography sx={{ fontSize: '0.82rem', color: checked ? 'text.primary' : 'text.secondary', fontWeight: checked ? 500 : 400, lineHeight: 1.4, userSelect: 'none' }}>{perm.label}</Typography>}
                      sx={{ ml: 0, mr: 0, display: 'flex', alignItems: 'center' }}
                    />
                  );
                })}
              </Box>
            </Box>
            {gi < PERMISSION_GROUPS.length - 1 && <Divider sx={{ borderColor: alpha(PRIMARY, 0.07) }} />}
          </Box>
        );
      })}
    </Box>
  );
}

// ── Shared table head (same widths in every group) ────────────────────────────
function RolesTableHead() {
  return (
    <TableHead>
      <TableRow>
        <TableCell sx={{ width: COL.role.width }}>Role</TableCell>
        <TableCell sx={{ width: COL.type.width }}>Type</TableCell>
        <TableCell sx={{ width: COL.perms.width }}>Permissions</TableCell>
        <TableCell sx={{ width: COL.actions.width }} align="right">Actions</TableCell>
      </TableRow>
    </TableHead>
  );
}

// ── Role row ──────────────────────────────────────────────────────────────────
function RoleRow({ role, onEdit, onDelete }) {
  const granted = role.isSuperAdmin ? ALL_KEYS.length : ALL_KEYS.filter(k => role.permissions?.[k]).length;
  return (
    <TableRow hover onClick={() => onEdit(role)} sx={{ cursor: 'pointer', '&:hover': { background: alpha(PRIMARY, 0.025) } }}>
      <TableCell sx={{ width: COL.role.width }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: '50%', background: role.color || '#607D8B', flexShrink: 0 }} />
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>{role.name}</Typography>
            {role.description && <Typography variant="caption" color="text.secondary">{role.description}</Typography>}
          </Box>
        </Box>
      </TableCell>
      <TableCell sx={{ width: COL.type.width }}>
        {role.isSuperAdmin
          ? <Chip label="Super Admin" size="small" sx={{ fontSize: '0.7rem', height: 20, background: alpha(PRIMARY, 0.1), color: PRIMARY, fontWeight: 700, border: `1px solid ${alpha(PRIMARY, 0.2)}` }} />
          : role.isSystem
          ? <Chip label="System"      size="small" sx={{ fontSize: '0.7rem', height: 20, background: alpha('#888', 0.08), color: '#666' }} />
          : <Chip label="Custom"      size="small" sx={{ fontSize: '0.7rem', height: 20, background: alpha('#00a882', 0.08), color: '#00a882' }} />
        }
      </TableCell>
      <TableCell sx={{ width: COL.perms.width }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ height: 5, flex: 1, maxWidth: 90, background: alpha(PRIMARY, 0.1), borderRadius: 3, overflow: 'hidden' }}>
            <Box sx={{ height: '100%', width: `${(granted / ALL_KEYS.length) * 100}%`, background: role.isSuperAdmin ? '#00a882' : PRIMARY, borderRadius: 3, transition: 'width 0.3s' }} />
          </Box>
          <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', whiteSpace: 'nowrap' }}>
            {granted}/{ALL_KEYS.length}
          </Typography>
        </Box>
      </TableCell>
      <TableCell sx={{ width: COL.actions.width }} align="right">
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
          <Tooltip title="Edit permissions">
            <IconButton size="small" onClick={e => { e.stopPropagation(); onEdit(role); }}>
              <EditOutlined sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          {!role.isSystem && (
            <Tooltip title="Delete role">
              <IconButton size="small" onClick={e => { e.stopPropagation(); onDelete(role, e); }} sx={{ '&:hover': { color: '#d32f2f' } }}>
                <DeleteOutlined sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </TableCell>
    </TableRow>
  );
}

// ── Org group — with its own page state ───────────────────────────────────────
function OrgGroup({ org, roles, onEdit, onDelete }) {
  const [open, setOpen]   = useState(true);
  const [page, setPage]   = useState(1);
  const color             = org.primaryColor || PRIMARY;
  const totalPages        = Math.ceil(roles.length / PAGE_SIZE);
  const paginated         = roles.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <Card sx={{ borderRadius: 3, border: `1px solid ${alpha(color, 0.18)}`, overflow: 'hidden', mb: 2 }}>
      {/* Group header */}
      <Box onClick={() => setOpen(v => !v)} sx={{
        display: 'flex', alignItems: 'center', gap: 2, px: 2.5, py: 1.75,
        background: open ? alpha(color, 0.04) : '#fff',
        borderBottom: open ? `1px solid ${alpha(color, 0.1)}` : 'none',
        cursor: 'pointer', transition: 'background 0.15s',
        '&:hover': { background: alpha(color, 0.07) },
      }}>
        {org.logo ? (
          <Box sx={{ width: 32, height: 32, borderRadius: 1.5, overflow: 'hidden', border: `1px solid ${alpha(color, 0.2)}`, flexShrink: 0, background: '#fff' }}>
            <img src={org.logo} alt={org.name} style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 3 }} />
          </Box>
        ) : (
          <Avatar variant="rounded" sx={{ width: 32, height: 32, borderRadius: 1.5, bgcolor: alpha(color, 0.12), color, fontSize: '0.85rem', fontWeight: 800, flexShrink: 0 }}>
            {org.name?.[0]?.toUpperCase()}
          </Avatar>
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: 'text.primary', lineHeight: 1.2 }}>{org.name}</Typography>
          <Typography variant="caption" color="text.secondary">
            {roles.length} role{roles.length !== 1 ? 's' : ''}
            {totalPages > 1 && ` · page ${page}/${totalPages}`}
          </Typography>
        </Box>
        <IconButton size="small" sx={{ color: 'text.secondary', pointerEvents: 'none' }}>
          {open ? <ExpandLessOutlined sx={{ fontSize: 18 }} /> : <ExpandMoreOutlined sx={{ fontSize: 18 }} />}
        </IconButton>
      </Box>

      {open && (
        <>
          <TableContainer>
            <Table sx={{ tableLayout: 'fixed' }}>
              <RolesTableHead />
              <TableBody>
                {paginated.map(role => (
                  <RoleRow key={role._id} role={role} onEdit={onEdit} onDelete={onDelete} />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Pagination page={page} totalPages={totalPages} onChange={setPage} />
        </>
      )}
    </Card>
  );
}

// ── Main tab ──────────────────────────────────────────────────────────────────
export default function RolesTab({ hideTitle = false }) {
  const [roles, setRoles]           = useState([]);
  const [orgs, setOrgs]             = useState([]);
  const [loading, setLoading]       = useState(true);
  const [search, setSearch]         = useState('');
  const [orgFilter, setOrgFilter]   = useState('');
  const [error, setError]           = useState('');
  const [panel, setPanel]           = useState(null);
  const [activeRole, setActiveRole] = useState(null);
  const [localPerms, setLocalPerms] = useState({});
  const [createForm, setCreateForm] = useState({ organizationId: '', name: '', description: '', color: '#607D8B' });
  const [createError, setCreateError] = useState('');
  const [creating, setCreating]     = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [rRes, oRes] = await Promise.all([api.get('/roles'), api.get('/organizations')]);
      setRoles(rRes.data.data || []);
      setOrgs(oRes.data.data || []);
    } catch (e) { setError(e?.response?.data?.message || 'Failed to load'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filteredRoles = roles.filter(r => {
    const ms = !search || r.name.toLowerCase().includes(search.toLowerCase());
    const mo = !orgFilter || r.organization?._id === orgFilter;
    return ms && mo;
  });

  const grouped = orgs
    .map(org => ({ org, roles: filteredRoles.filter(r => r.organization?._id === org._id || r.organization === org._id) }))
    .filter(g => g.roles.length > 0);

  const unattached = filteredRoles.filter(r => !orgs.some(o => o._id === r.organization?._id || o._id === r.organization));

  const openCreate = () => { setCreateForm({ organizationId: '', name: '', description: '', color: '#607D8B' }); setCreateError(''); setCreating(false); setPanel('create'); };
  const openEdit   = (role) => { setActiveRole(role); setLocalPerms({ ...(role.permissions || {}) }); setPanel('edit'); };
  const closePanel = () => { setPanel(null); setActiveRole(null); };

  const handleCreate = async () => {
    if (!createForm.organizationId || !createForm.name) { setCreateError('Organization and name are required'); return; }
    setCreating(true); setCreateError('');
    try { const { data } = await api.post('/roles', createForm); setRoles(prev => [data.data, ...prev]); closePanel(); }
    catch (e) { setCreateError(e?.response?.data?.message || 'Failed'); setCreating(false); }
  };

  const patchPerms = async (updates) => {
    if (!activeRole || activeRole.isSuperAdmin) return;
    setLocalPerms(p => ({ ...p, ...updates }));
    try {
      await api.patch(`/roles/${activeRole._id}/permissions`, { permissions: updates });
      setRoles(prev => prev.map(r => r._id === activeRole._id ? { ...r, permissions: { ...r.permissions, ...updates } } : r));
    } catch {
      setLocalPerms(p => ({ ...p, ...Object.fromEntries(Object.entries(updates).map(([k, v]) => [k, !v])) }));
    }
  };

  const handleToggle      = (key, value) => patchPerms({ [key]: value });
  const handleToggleGroup = (group, value) => patchPerms(Object.fromEntries(group.permissions.map(p => [p.key, value])));

  const handleDelete = async (role, e) => {
    e.stopPropagation();
    if (role.isSystem) return;
    if (!window.confirm(`Delete role "${role.name}"?`)) return;
    try { await api.delete(`/roles/${role._id}`); setRoles(prev => prev.filter(r => r._id !== role._id)); if (activeRole?._id === role._id) closePanel(); }
    catch (e) { setError(e?.response?.data?.message || 'Delete failed'); }
  };

  const setC = (k, v) => setCreateForm(p => ({ ...p, [k]: v }));

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, gap: 2, flexWrap: 'wrap' }}>
        {!hideTitle
          ? <Box><Typography sx={{ fontWeight: 800, fontSize: '1.3rem', color: 'text.primary' }}>Roles & Permissions</Typography><Typography variant="body2" color="text.secondary">{loading ? 'Loading…' : `${roles.length} role${roles.length !== 1 ? 's' : ''}`}</Typography></Box>
          : <Typography variant="body2" color="text.secondary">{loading ? 'Loading…' : `${roles.length} role${roles.length !== 1 ? 's' : ''}`}</Typography>
        }
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          <TextField placeholder="Search roles…" size="small" value={search} onChange={e => setSearch(e.target.value)} sx={{ width: 180 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchOutlined sx={{ fontSize: 17 }} /></InputAdornment> }} />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Company</InputLabel>
            <Select value={orgFilter} label="Company" onChange={e => setOrgFilter(e.target.value)}>
              <MenuItem value="">All companies</MenuItem>
              {orgs.map(o => <MenuItem key={o._id} value={o._id}>{o.name}</MenuItem>)}
            </Select>
          </FormControl>
          <Button variant="contained" startIcon={<AddOutlined />} onClick={openCreate} sx={{ borderRadius: 2 }}>New Role</Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {loading ? (
        <Box>
          {[...Array(2)].map((_, gi) => (
            <Card key={gi} sx={{ borderRadius: 3, mb: 2, overflow: 'hidden' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 2.5, py: 1.75, borderBottom: `1px solid ${alpha(PRIMARY, 0.08)}` }}>
                <Skeleton variant="rounded" width={32} height={32} sx={{ borderRadius: 1.5 }} />
                <Box sx={{ flex: 1 }}><Skeleton width="30%" /><Skeleton width="15%" height={12} /></Box>
              </Box>
              <Box sx={{ p: 2 }}>
                {[...Array(3)].map((_, i) => (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.2 }}>
                    <Skeleton variant="circular" width={10} height={10} />
                    <Box sx={{ flex: 1 }}><Skeleton width="35%" /></Box>
                    <Skeleton width={70} height={20} sx={{ borderRadius: 1 }} />
                    <Skeleton width={100} height={5} sx={{ borderRadius: 3 }} />
                    <Skeleton width={50} />
                  </Box>
                ))}
              </Box>
            </Card>
          ))}
        </Box>
      ) : filteredRoles.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 10 }}>
          <ShieldOutlined sx={{ fontSize: 48, color: alpha(PRIMARY, 0.2), mb: 2 }} />
          <Typography color="text.secondary">No roles found.</Typography>
        </Box>
      ) : (
        <Box>
          {grouped.map(({ org, roles: orgRoles }) => (
            <OrgGroup key={org._id} org={org} roles={orgRoles} onEdit={openEdit} onDelete={handleDelete} />
          ))}

          {unattached.length > 0 && (
            <Card sx={{ borderRadius: 3, border: `1px solid ${alpha('#888', 0.15)}`, overflow: 'hidden', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 2.5, py: 1.75, background: alpha('#888', 0.04), borderBottom: `1px solid ${alpha('#888', 0.1)}` }}>
                <Avatar variant="rounded" sx={{ width: 32, height: 32, borderRadius: 1.5, bgcolor: alpha('#888', 0.12), color: '#888', flexShrink: 0 }}>
                  <BusinessOutlined sx={{ fontSize: 18 }} />
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: 'text.secondary' }}>Unassigned</Typography>
                  <Typography variant="caption" color="text.secondary">{unattached.length} role{unattached.length !== 1 ? 's' : ''}</Typography>
                </Box>
              </Box>
              <TableContainer>
                <Table sx={{ tableLayout: 'fixed' }}>
                  <RolesTableHead />
                  <TableBody>
                    {unattached.map(role => <RoleRow key={role._id} role={role} onEdit={openEdit} onDelete={handleDelete} />)}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          )}
        </Box>
      )}

      {/* ── Create panel ──────────────────────────────────────────────────── */}
      <SlidePanel open={panel === 'create'} onClose={closePanel} title="New Role" subtitle="Set a name and assign it to a company" width={460}
        footer={<><Button onClick={closePanel} variant="outlined" sx={{ borderRadius: 2 }}>Cancel</Button><Button onClick={handleCreate} variant="contained" disabled={creating} sx={{ borderRadius: 2 }}>{creating ? 'Creating…' : 'Create Role'}</Button></>}
      >
        {createError && <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }}>{createError}</Alert>}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <Box>
            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1, display: 'block' }}>Assignment</Typography>
            <FormControl size="small" fullWidth>
              <InputLabel>Company *</InputLabel>
              <Select value={createForm.organizationId} label="Company *" onChange={e => setC('organizationId', e.target.value)}>
                {orgs.map(o => <MenuItem key={o._id} value={o._id}>{o.name}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>
          <Divider sx={{ borderColor: alpha(PRIMARY, 0.08) }} />
          <Box>
            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1, display: 'block' }}>Role Details</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <TextField label="Role Name *" value={createForm.name} onChange={e => setC('name', e.target.value)} fullWidth size="small" />
              <TextField label="Description" value={createForm.description} onChange={e => setC('description', e.target.value)} fullWidth size="small" />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>Badge color</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{ width: 28, height: 28, borderRadius: 1.5, background: createForm.color, border: '2px solid rgba(0,0,0,0.1)' }} />
                  <input type="color" value={createForm.color} onChange={e => setC('color', e.target.value)} style={{ width: 44, height: 34, border: '1px solid rgba(16,117,186,0.2)', borderRadius: 8, cursor: 'pointer', padding: 2, background: 'white' }} />
                </Box>
              </Box>
            </Box>
          </Box>
          <Box sx={{ background: alpha(PRIMARY, 0.04), border: `1px solid ${alpha(PRIMARY, 0.12)}`, borderRadius: 2, p: 2 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>Permissions are configured after creation — click the role row to open the permissions editor.</Typography>
          </Box>
        </Box>
      </SlidePanel>

      {/* ── Edit permissions panel ────────────────────────────────────────── */}
      <SlidePanel open={panel === 'edit'} onClose={closePanel} title={activeRole?.name || 'Role Permissions'} subtitle={activeRole?.organization?.name || ''} width={520}
        footer={<Button onClick={closePanel} variant="contained" sx={{ borderRadius: 2 }}>Done</Button>}
      >
        {activeRole && (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5, pb: 2.5, borderBottom: `1px solid ${alpha(PRIMARY, 0.08)}` }}>
              <Box sx={{ width: 12, height: 12, borderRadius: '50%', background: activeRole.color || '#607D8B', flexShrink: 0 }} />
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>{activeRole.name}</Typography>
                {activeRole.description && <Typography variant="caption" color="text.secondary">{activeRole.description}</Typography>}
              </Box>
              {activeRole.isSuperAdmin && <Chip label="Super Admin" size="small" sx={{ fontSize: '0.7rem', height: 20, background: alpha(PRIMARY, 0.1), color: PRIMARY, fontWeight: 700 }} />}
            </Box>
            {activeRole.isSuperAdmin ? (
              <Box sx={{ textAlign: 'center', py: 5 }}>
                <ShieldOutlined sx={{ fontSize: 42, color: alpha(PRIMARY, 0.25), mb: 1.5, display: 'block', mx: 'auto' }} />
                <Typography variant="body2" color="text.secondary">Super Admin roles have full access to all permissions and cannot be restricted.</Typography>
              </Box>
            ) : (
              <>
                <PermissionsList perms={localPerms} isSuperAdmin={activeRole.isSuperAdmin} onToggle={handleToggle} onToggleGroup={handleToggleGroup} />
                <Typography variant="caption" sx={{ color: 'text.secondary', mt: 2, display: 'block' }}>Changes are saved automatically.</Typography>
              </>
            )}
          </Box>
        )}
      </SlidePanel>
    </Box>
  );
}